/**
 * MergedDocumentPage Component (TASK-188, AC-3 / FR-004)
 *
 * Read-only viewer that shows the merged + annotated document exactly as the
 * segmenter "sees" it: source headers, service-start / -end markers and orphan
 * text that didn't fall into any service.
 *
 * Data comes from a dedicated api layer module (`mergedDocumentApi.get()` →
 * GET /api/v1/merged-document/). The annotated_content carries TEXT markers
 * inserted by the backend; the page renders it LINE BY LINE, classifying each
 * line into one of four kinds:
 *   - service start  `<<< Услуга N: «Имя» [тип]`   → highlighted, colored by тип
 *   - service end    `>>> конец Услуги N`           → muted accent
 *   - source header  `=== ИСТОЧНИК: Name (TYPE) ===` → section header
 *   - everything else                                → plain / orphan line
 *
 * Clicking a matched service-start marker navigates to /services.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, FileText } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { mergedDocumentApi } from '@/services/api/mergedDocument';
import type { Boundary } from '@/services/api/mergedDocument';

// ── Type → color mapping ────────────────────────────────────────────────────
// Consistent with the /services badge semantics: distinct, accessible Tailwind
// classes per service type. `справочник` (reference) is neutral gray; unknown
// types fall back to slate.
const TYPE_COLORS: Record<string, string> = {
  калькулятор: 'bg-blue-100 text-blue-800',
  'прайс-лист': 'bg-amber-100 text-amber-800',
  классификатор: 'bg-purple-100 text-purple-800',
  справочник: 'bg-gray-100 text-gray-700',
};

const FALLBACK_TYPE_COLOR = 'bg-slate-100 text-slate-800';

function typeColorClass(type: string): string {
  return TYPE_COLORS[type.trim().toLowerCase()] ?? FALLBACK_TYPE_COLOR;
}

// ── Marker parsing ──────────────────────────────────────────────────────────
const SERVICE_START_PREFIX = '<<< Услуга ';
const SERVICE_END_PREFIX = '>>> конец Услуги';
const SOURCE_PREFIX = '=== ИСТОЧНИК:';

interface ParsedStart {
  /** Type word inside [...] (e.g. «калькулятор»), '' if absent. */
  type: string;
  /** Service name inside «...», '' if absent. */
  name: string;
}

/**
 * Parse a `<<< Услуга N: «Имя» [тип]` line.
 * Both the name («...») and type ([...]) are optional → graceful fallback.
 */
function parseServiceStart(line: string): ParsedStart {
  const nameMatch = line.match(/«([^»]*)»/);
  const typeMatch = line.match(/\[([^\]]*)\]/);
  return {
    name: nameMatch ? nameMatch[1].trim() : '',
    type: typeMatch ? typeMatch[1].trim() : '',
  };
}

type LineKind = 'service-start' | 'service-end' | 'source' | 'plain';

function classifyLine(line: string): LineKind {
  if (line.startsWith(SERVICE_START_PREFIX)) return 'service-start';
  if (line.startsWith(SERVICE_END_PREFIX)) return 'service-end';
  if (line.startsWith(SOURCE_PREFIX)) return 'source';
  return 'plain';
}

// ── Document renderer ───────────────────────────────────────────────────────
function DocumentBody({
  annotatedContent,
  boundaries,
  onMatchedServiceClick,
}: {
  annotatedContent: string;
  boundaries: Boundary[];
  onMatchedServiceClick: (boundary: Boundary) => void;
}): JSX.Element {
  // Index boundaries by service name for O(1) lookup when a start marker is
  // clicked. Names are unique enough in practice; first-wins keeps it resilient.
  // Memoized on `boundaries` so the Map is not rebuilt on every render.
  const boundaryByName = useMemo(() => {
    const map = new Map<string, Boundary>();
    boundaries.forEach(b => {
      if (!map.has(b.service_name)) {
        map.set(b.service_name, b);
      }
    });
    return map;
  }, [boundaries]);

  // Split the annotated document into lines once per content change. The doc can
  // be tens of thousands of chars (NFR-1) — avoid re-splitting on every render.
  const lines = useMemo(() => annotatedContent.split('\n'), [annotatedContent]);

  return (
    <div
      data-testid="merged-document-content"
      className="font-mono text-xs whitespace-pre-wrap overflow-auto max-h-[70vh] rounded-lg border bg-white shadow-sm p-4 leading-relaxed">
      {lines.map((line, idx) => {
        const kind = classifyLine(line);

        if (kind === 'service-start') {
          const { name, type } = parseServiceStart(line);
          const boundary = boundaryByName.get(name);
          const matched = boundary?.matched === true;
          return (
            <button
              key={idx}
              type="button"
              data-testid="marker-service-start"
              data-service-type={type}
              // Non-navigable markers (no matching boundary or matched===false)
              // are inert: mark them disabled so screen readers don't announce
              // them as actionable. Matched markers stay clickable.
              disabled={!matched}
              aria-disabled={!matched}
              onClick={() => {
                if (boundary && matched) onMatchedServiceClick(boundary);
              }}
              className={`block w-full text-left rounded px-2 py-0.5 my-0.5 font-bold ${typeColorClass(
                type
              )} ${matched ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
              title={matched ? 'Перейти к услуге' : 'Скоуп не сопоставлен с услугой'}>
              {line}
            </button>
          );
        }

        if (kind === 'service-end') {
          return (
            <div
              key={idx}
              data-testid="marker-service-end"
              className="px-2 py-0.5 my-0.5 text-gray-400 italic">
              {line}
            </div>
          );
        }

        if (kind === 'source') {
          return (
            <div
              key={idx}
              data-testid="marker-source"
              className="px-2 py-1 my-1 rounded bg-slate-800 text-slate-100 font-bold">
              {line}
            </div>
          );
        }

        // Plain / orphan line. Preserve empty lines so the layout does not
        // collapse — render a non-breaking space when the line is blank.
        return (
          <div key={idx} className="px-2 text-gray-700">
            {line === '' ? ' ' : line}
          </div>
        );
      })}
    </div>
  );
}

// ── Legend ──────────────────────────────────────────────────────────────────
function Legend(): JSX.Element {
  const swatches: { type: string; label: string }[] = [
    { type: 'калькулятор', label: 'калькулятор' },
    { type: 'прайс-лист', label: 'прайс-лист' },
    { type: 'классификатор', label: 'классификатор' },
    { type: 'справочник', label: 'справочник' },
  ];

  return (
    <div
      data-testid="merged-document-legend"
      className="rounded-lg border bg-white shadow-sm p-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        {swatches.map(s => (
          <span key={s.type} className="flex items-center gap-1.5">
            <span
              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${typeColorClass(s.type)}`}>
              {s.label}
            </span>
          </span>
        ))}
      </div>
      <p className="text-gray-500">Текст вне маркеров не попал ни в одну услугу.</p>
      <p className="text-gray-500">· vision — фрагмент получен из транскрипции скриншота страницы.</p>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export function MergedDocumentPage(): JSX.Element {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Итоговый документ - SMETA AI';
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['mergedDocument'],
    queryFn: mergedDocumentApi.get,
    retry: false,
  });

  // Distinguish a 404 (not indexed yet) from a generic error.
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  const isNotIndexed = Boolean(error) && status === 404;
  const isGenericError = Boolean(error) && status !== 404;

  return (
    <PageLayout
      data-testid="merged-document-page"
      title="Итоговый документ"
      subtitle={
        <p className="text-sm text-gray-500">
          <span>Как сегментатор видит объединённый документ</span>
          <span className="hidden sm:inline">{' — с разметкой границ услуг'}</span>
        </p>
      }
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <FileText className="h-5 w-5 text-white" />
        </div>
      }
      breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Документ' }]}>
      {/* Loading state */}
      {isLoading && (
        <div data-testid="merged-document-loading" className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Загрузка документа...</span>
        </div>
      )}

      {/* Not-indexed (404) state */}
      {!isLoading && isNotIndexed && (
        <div
          data-testid="merged-document-empty"
          className="flex flex-col items-center justify-center py-12 text-gray-400">
          <FileText className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">Документ ещё не сформирован — выполните индексацию источников</p>
        </div>
      )}

      {/* Generic error state */}
      {!isLoading && isGenericError && (
        <div
          data-testid="merged-document-error"
          className="flex items-center justify-center py-12 text-red-500">
          <AlertCircle className="h-6 w-6 mr-2" />
          <span>Ошибка загрузки документа</span>
        </div>
      )}

      {/* Success state */}
      {!isLoading && !error && data && (
        <div className="space-y-4">
          <Legend />
          <DocumentBody
            annotatedContent={data.annotated_content}
            boundaries={data.boundaries}
            onMatchedServiceClick={() => navigate('/services')}
          />
        </div>
      )}
    </PageLayout>
  );
}

export default MergedDocumentPage;
